import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { AchievementGroup } from "../components/achievements/achievement-group";
import { SideCannons } from "../components/ui/confetti";
import { ACHIEVEMENTS_BY_STAT } from "../data/achievements/config";
import {
  useAchievementRewards,
  useAchievements,
} from "../data/achievements/hooks";
import { AchievementId } from "../data/achievements/types";

export const Route = createFileRoute("/achievements")({
  component: RouteComponent,
});

// Define sport categories for filtering
type SportCategory = "all" | "core" | "running" | "walking" | "biking";

function RouteComponent() {
  const {
    data: achievements,
    isLoading: achievementsLoading,
    error: achievementsError,
  } = useAchievements();
  const {
    data: claimedRewards,
    isLoading: rewardsLoading,
    error: rewardsError,
  } = useAchievementRewards();
  const [activeCategory, setActiveCategory] = useState<SportCategory>("all");
  const [showConfetti, setShowConfetti] = useState(false);

  // Handler for when rewards are claimed
  const handleClaimRewards = (rewards: string[]) => {
    if (rewards.length > 0) {
      setShowConfetti(true);
    }
  };

  if (achievementsLoading || rewardsLoading) {
    return <div>Loading achievements...</div>;
  }

  if (achievementsError) {
    return <div>Error loading achievements: {achievementsError.message}</div>;
  }

  if (rewardsError) {
    return <div>Error loading achievement rewards: {rewardsError.message}</div>;
  }

  // Group achievements by their stat field
  const groupedAchievements: Record<string, AchievementId[]> = {};

  if (achievements) {
    // Iterate through all achievements and group them by stat
    Object.entries(achievements).forEach(([id, achievement]) => {
      // Skip custom achievements that aren't completed
      if (
        achievement.custom &&
        !(achievement.progress ?? 0 >= achievement.goal)
      ) {
        return;
      }

      const stat = achievement.stat;
      if (!groupedAchievements[stat]) {
        groupedAchievements[stat] = [];
      }
      groupedAchievements[stat].push(id as AchievementId);
    });
  }

  // Create achievement groups based on the stat grouping
  const achievementGroups = Object.entries(groupedAchievements).map(
    ([stat, ids]) => {
      // Get the configuration for this stat, or use a default if not found
      const config = ACHIEVEMENTS_BY_STAT[
        stat as keyof typeof ACHIEVEMENTS_BY_STAT
      ] || {
        title: stat,
        description: `Achievements related to ${stat}`,
        unit: "",
        category: "core",
      };

      return {
        id: stat,
        title: config.title,
        description: config.description,
        ids: ids,
        unit: config.unit,
        category: config.category,
      };
    }
  );

  // Filter groups to only include those with available achievements
  const availableGroups = achievementGroups.filter((group) => {
    if (!achievements) return false;
    return group.ids.some((id) => achievements[id]);
  });

  return (
    <div className="p-4 bg-gradient-to-br from-[#2b7fff]/10 to-[#fe9a00]/10 w-full h-full">
      {showConfetti && (
        <SideCannons
          particleCount={80}
          startVelocity={60}
          duration={5000}
          spread={100}
          colors={["#FFD700", "#FFA500", "#FF4500", "#9370DB", "#4169E1"]}
          onComplete={() => setShowConfetti(false)}
        />
      )}
      <div className="mb-4 text-center sm:text-left">
        <h1 className="text-3xl font-bold bg-gradient-to-r from-[#2b7fff] to-[#fe9a00] bg-clip-text text-transparent">
          Achievements
        </h1>
        <p className="text-gray-500 text-base md:text-lg mb-1 sm:mb-0">
          Earn achievements by completing their requirements.
        </p>
      </div>

      <div className="max-w-screen-xl mx-auto">
        {/* Category tabs */}
        <div className="flex mb-6 border-b border-gray-200 overflow-x-auto">
          <button
            onClick={() => setActiveCategory("all")}
            className={`px-4 py-2 font-medium text-base transition-colors ${
              activeCategory === "all"
                ? "text-[#fe9a00] border-b-2 border-[#fe9a00]"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            All
          </button>
          <button
            onClick={() => setActiveCategory("core")}
            className={`px-4 py-2 font-medium text-base transition-colors ${
              activeCategory === "core"
                ? "text-[#fe9a00] border-b-2 border-[#fe9a00]"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Core
          </button>
          <button
            onClick={() => setActiveCategory("running")}
            className={`px-4 py-2 font-medium text-base transition-colors ${
              activeCategory === "running"
                ? "text-[#fe9a00] border-b-2 border-[#fe9a00]"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Running
          </button>
          <button
            onClick={() => setActiveCategory("walking")}
            className={`px-4 py-2 font-medium text-base transition-colors ${
              activeCategory === "walking"
                ? "text-[#fe9a00] border-b-2 border-[#fe9a00]"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Walking
          </button>
          <button
            onClick={() => setActiveCategory("biking")}
            className={`px-4 py-2 font-medium text-base transition-colors ${
              activeCategory === "biking"
                ? "text-[#fe9a00] border-b-2 border-[#fe9a00]"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Biking
          </button>
        </div>

        {/* Display achievements by logical groups */}
        <div className="grid grid-cols-1 gap-8">
          {availableGroups
            .filter((group) => {
              if (activeCategory === "all") return true;
              return group.category === activeCategory;
            })
            .map((group) => {
              if (!achievements) return null;

              // Only include achievements that exist in the data
              const validIds = group.ids.filter((id) => achievements[id]);

              if (validIds.length === 0) return null;

              // Sort the achievement IDs by goal value
              const sortedIds = [...validIds].sort((a, b) => {
                return achievements[a].goal - achievements[b].goal;
              });

              // Find the current achievement (first one that's not completed)
              const currentAchievementIndex = sortedIds.findIndex((id) => {
                const progress = achievements[id].progress ?? 0;
                return progress < achievements[id].goal;
              });

              // Get the progress for the current achievement
              let currentProgress = 0;

              if (currentAchievementIndex !== -1) {
                // We found an incomplete achievement, use its progress
                const currentId = sortedIds[currentAchievementIndex];
                currentProgress = achievements[currentId].progress ?? 0;
              } else if (sortedIds.length > 0) {
                // All achievements are complete, use the last one's progress
                const lastId = sortedIds[sortedIds.length - 1];
                currentProgress = achievements[lastId].progress ?? 0;
              }

              // Create the achievement group data structure
              const achievementGroup: AchievementGroup = {
                progress: currentProgress,
                steps: sortedIds.map((id) => ({
                  id,
                  name: achievements[id].name,
                  description: achievements[id].description,
                  goal: achievements[id].goal,
                  rewards: achievements[id].rewards,
                })),
              };

              return (
                <AchievementGroup
                  key={group.id}
                  groupName={group.title}
                  groupDescription={group.description}
                  unit={group.unit}
                  achievementGroup={achievementGroup}
                  onClaimRewards={handleClaimRewards}
                  claimedRewards={claimedRewards || []}
                />
              );
            })}
        </div>
      </div>
    </div>
  );
}
